/* jshint esversion: 6 */
/* globals angular, IB, URL, $ */

/**
 * CMSMediaUploadService is a service class for handling media file uploads in a CMS system.
 * It manages file queues, checks file types, and handles file uploads.
 */
class CMSMediaUploadService {
    /**
     * Constructor for CMSMediaUploadService.
     * @param {Object} $http - Angular's $http service for making HTTP requests.
     * @param {Object} $bridges - Service for accessing bridge functionalities.
     * @param {Object} $rootScope - Angular's $rootScope for broadcasting events.
     * @param {Object} $signals - Service for emitting signals.
     * @param {Object} $timeout - Angular's $timeout service for deferred execution.
     */
    constructor($http, $bridges, $rootScope, $signals, $timeout) {
        // 'ngInject'; // Remove this line if present
        this.$http = $http;
        this.$bridges = $bridges;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.$timeout = $timeout;

        this._logIdentifier = 'CMSMediaUploadService';
        this.$logger = $bridges.logger();
        this.$logger.logEnter(this._logIdentifier, 'initialize');

        this._files = [];
        window.ff = this._files; // SMELL: Exposing internal state to window object for debugging.
    }

    /**
     * Checks if a file is already in the upload queue.
     * @param {Object} file - The file object to check.
     * @returns {boolean} - True if the file is already in the queue, false otherwise.
     */
    isAlreadyInQueue(file) {
        for (let idx = 0; idx < this._files.length; idx++) {
            if (typeof this._files[idx].files[0].uuid !== 'undefined' && this._files[idx].files[0].uuid === file.files[0].uuid) {
                return true;
            }
        }
        return false;
    }

    /**
     * Checks if the file type is acceptable based on its MIME type and extension.
     * @param {Object} singleFileObject - The file object to check.
     * @returns {boolean} - True if the file type is acceptable, false otherwise.
     */
    checkFileType(singleFileObject) {
        let _extension = singleFileObject.name.substring(singleFileObject.name.lastIndexOf(".")+1).toLowerCase(),
            _accepted_filetypes = /^image\/(gif|jpe?g|png|svg)|video\/(mp4|webm|avi|x-ms-asf|quicktime)|audio\/(mp3|mpeg)|application\/(zip|vnd.geogebra.file)$/i;

        if (typeof singleFileObject.type === 'undefined') {
            return false;
        } else if (_extension === "srt" || _extension === "zip" || _extension === "ggb") {
            return true;
        } else if (!_accepted_filetypes.test(singleFileObject.type)) {
            return false;
        }
        return true;
    }

    /**
     * Adds a file to the upload queue.
     * @param {Object} file - The file object to add.
     */
    add(file) {
        console.log('FILES ADD ==>', file);

        if (typeof(file.files) !== 'undefined' && this.checkFileType(file.files[0])) {

            let uuid = angular.createUUID(),
                ext  = file.files[0].name.substring(file.files[0].name.lastIndexOf(".")+1).toLowerCase();

            if (file.files[0].type.startsWith('video')) {
                file.files[0].thumbnail = '/images/cms/dummy-video-thumb-in-queue.jpg';
            } else if (file.files[0].type.startsWith('audio')) {
                file.files[0].thumbnail = '/images/cms/dummy-audio-thumb.jpg';
            } else if (file.files[0].type === '' && ext === 'srt') {
                file.files[0].thumbnail = '/images/cms/dummy-subtitle-thumb.jpg';
            } else if (ext === 'zip') {
                file.files[0].thumbnail = '/images/cms/dummy-iframecontent-thumb.jpg';
            } else if (ext === 'ggb') {
                file.files[0].thumbnail = '/images/cms/dummy-ggb-thumb.jpg';
            } else {
                file.files[0].thumbnail = URL.createObjectURL(file.files[0]);
            }

            file.files[0].progresspercentage = 0;
            file.files[0].state = undefined;
            file.files[0].uuid  = uuid;
            file.files[0].sizeSI = angular.bytesToSize(file.files[0].size);
            file.files[0].sizeIEC = angular.bytesToSize(file.files[0].size, true);

            console.log('FILES UUID ===>', uuid);

            file.percentage = undefined;
            this._files.push(file);

            this.$signals.emit(IB.Signals.UPLOAD_SERVICE_FILE_ADDED, file.files[0].uuid);
        } else if (file) {
            let uuid = angular.createUUID(),
                ext  = file.name.substring(file.name.lastIndexOf(".")+1).toLowerCase();

            if (file.type.startsWith('video')) {
                file.thumbnail = '/images/cms/dummy-video-thumb-in-queue.jpg';
            } else if (file.type.startsWith('audio')) {
                file.thumbnail = '/images/cms/dummy-audio-thumb.jpg';
            } else if (file.type === '' && ext === 'srt') {
                file.thumbnail = '/images/cms/dummy-subtitle-thumb.jpg';
            } else if (ext === 'zip') {
                file.thumbnail = '/images/cms/dummy-iframecontent-thumb.jpg';
            } else if (ext === 'ggb') {
                file.thumbnail = '/images/cms/dummy-ggb-thumb.jpg';
            } else {
                file.thumbnail = URL.createObjectURL(file);
            }

            file.progresspercentage = 0;
            file.state = undefined;
            file.uuid  = uuid;
            file.sizeSI = angular.bytesToSize(file.size);
            file.sizeIEC = angular.bytesToSize(file.size, true);

            console.log('FILES UUID ===>', uuid);

            file.percentage = undefined;
            this._files.push(file);

            this.$signals.emit(IB.Signals.UPLOAD_SERVICE_FILE_ADDED, file.uuid);
        }
    }

    /**
     * Removes a file from the upload queue by its UUID.
     * @param {string} fileUUID - The UUID of the file to remove.
     */
    remove(fileUUID) {
        if (typeof fileUUID === 'undefined') {
            this.$logger.error(this._logIdentifier, 'file uuid is not defined!');
        } else {
            for (let i = this._files.length - 1; i >= 0; i--) {
                if (typeof(this._files[i].files) !== 'undefined') {
                    if (this._files[i].files[0].uuid === fileUUID) {
                        this._files.splice(i, 1);
                    }
                } else {
                    if (this._files[i].uuid === fileUUID) {
                        this._files.splice(i, 1);
                    }
                }
            }
        }
    }

    /**
     * Clears the entire upload queue.
     */
    clearList() {
        this._files = [];
    }

    /**
     * Retrieves the list of files in the upload queue.
     * @returns {Array} - List of files in the upload queue.
     */
    files() {
        let fileList = [];

        angular.forEach(this._files, (value) => {
            console.log('FILES ==>', value);

            let _progress, _state, _status, _message, _detail;

            if (typeof(value._response) !== 'undefined' && typeof value._response.jqXHR !== 'undefined' && value._response.jqXHR.status === 400) {

                let _resp = value._response.jqXHR;

                if (_resp.hasOwnProperty("responseJSON")) {
                    _message = _resp.responseJSON.error;
                } else if (_resp.hasOwnProperty("responseText")) {
                    let _p = $.parseJSON(_resp.responseText);
                    _message = _p.error;
                } else {
                    _message = "There was an error when uploading this file";
                }

                _progress = {};
                _state = {};
                _status = value._response.jqXHR.status;
                _detail = value._response.errorThrown;

            } else {
                _progress = typeof value.progress !== "undefined" ? value.progress() : null;
                _state = typeof value.state !== "undefined" ? value.state() : null;
            }

            if (typeof(value.files) !== 'undefined') {
                fileList.push({
                    uuid: value.files[0].uuid,
                    name: value.files[0].name,
                    size: value.files[0].size,
                    sizeSI: value.files[0].sizeSI,
                    sizeIEC: value.files[0].sizeIEC,
                    thumbnail: value.files[0].thumbnail,
                    progresspercentage: value.files[0].progresspercentage,
                    progress: _progress,
                    state: _state,
                    serverStatus: _status,
                    serverMessage: _message,
                    serverDetail: _detail,
                    copiedUUID: value.files[0].uuid ? value.files[0].uuid : null
                });
            } else {
                fileList.push({
                    uuid: value.uuid,
                    name: value.name,
                    size: value.size,
                    sizeSI: value.sizeSI,
                    sizeIEC: value.sizeIEC,
                    thumbnail: value.thumbnail,
                    progresspercentage: value.progresspercentage,
                    progress: _progress,
                    state: _state,
                    serverStatus: _status,
                    serverMessage: _message,
                    serverDetail: _detail,
                    copiedUUID: value.uuid ? value.uuid : null
                });
            }

        });
        return fileList;
    }

    /**
     * Initiates the upload of a file by its UUID.
     * @param {Object} file - The file object to upload.
     */
    upload(file) {
        console.log('FIEL UPLOAD ==>', file);
        console.log('FILES UPLOAD ==>', this._files);
        for (let i = this._files.length - 1; i >= 0; i--) {
            if (typeof(this._files[i].files) !== 'undefined') {
                if (this._files[i].files[0].uuid === file.uuid) {
                    this._files[i].submit();
                }
            } else {
                if (this._files[i].uuid === file.uuid) {
                    this._files[i].submit();
                }
            }
        }
    }
}

// Add explicit dependency injection
CMSMediaUploadService.$inject = ['$http', '$bridges', '$rootScope', '$signals', '$timeout'];

export default CMSMediaUploadService;